/**
 * API Error Handling
 * Centralized error types and handlers
 */

import type { AxiosError } from 'axios';
import { z } from 'zod';

import { extractCurrencyErrorPayload, getCurrencyErrorCopy } from '@/lib/currency-error-messages';
import {
  extractIntangibleErrorCode,
  getIntangibleErrorCopy,
} from '@/lib/intangible-error-messages';
import {
  extractKaminoSyncErrorPayload,
  getKaminoSyncErrorCopy,
} from '@/lib/kamino-sync-error-messages';
import { getValidationErrorMessageByCode } from '@/lib/validation-error-messages';

/**
 * Standard API error response schema
 */
const ApiErrorBaseSchema = z.object({
  message: z.union([z.string(), z.array(z.string())]),
  statusCode: z.number().optional(),
  error: z.string().optional(),
  details: z.unknown().optional(),
});

/** Allows Nest/HTTP bodies that carry structured fields alongside `message` (e.g. NO_OPEN_PERIOD). */
export const ApiErrorSchema = ApiErrorBaseSchema.passthrough();

export type ApiError = z.infer<typeof ApiErrorSchema>;

/**
 * Custom error class for API errors
 */
export class ApiException extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public details?: unknown,
  ) {
    super(message);
    this.name = 'ApiException';
  }
}

function isAxiosLikeError(error: unknown): error is AxiosError<unknown> {
  return Boolean(error && typeof error === 'object' && 'isAxiosError' in error);
}

function hasStructuredValidationErrors(data: unknown): data is { errors: unknown[] } {
  return Boolean(
    data &&
    typeof data === 'object' &&
    !Array.isArray(data) &&
    Array.isArray((data as Record<string, unknown>).errors),
  );
}

/**
 * Parse Axios error into ApiException
 */
export function parseApiError(error: unknown): ApiException {
  if (error instanceof ApiException) {
    return error;
  }

  if (isAxiosLikeError(error)) {
    const axiosError = error as AxiosError<unknown>;

    // Try to parse error response
    const response = axiosError.response;
    if (response?.data) {
      if (hasStructuredValidationErrors(response.data)) {
        // Keep the body's own sentence when it has one: a coded refusal body (payment
        // dispatch, bulk validation) is more useful than the generic label.
        const bodyMessage = (response.data as { message?: unknown }).message;
        return new ApiException(
          typeof bodyMessage === 'string' && bodyMessage.trim() ? bodyMessage : 'Validation failed',
          response.status,
          response.data,
        );
      }

      const parsed = ApiErrorSchema.safeParse(response.data);
      if (parsed.success) {
        const data = parsed.data;
        const { error, ...dataWithoutHttpErrorKey } = data;
        void error;
        const { message: rawMessage, statusCode, details, ...rest } = dataWithoutHttpErrorKey;
        const message = Array.isArray(rawMessage) ? rawMessage.join('; ') : rawMessage;
        const mergedDetails =
          details !== undefined &&
          details !== null &&
          typeof details === 'object' &&
          !Array.isArray(details)
            ? { ...(details as Record<string, unknown>), ...rest }
            : Object.keys(rest).length > 0
              ? details !== undefined && details !== null
                ? { details, ...rest }
                : { ...rest }
              : details;
        return new ApiException(message, statusCode ?? response.status, mergedDetails);
      }
    }

    // Fallback to status text
    return new ApiException(
      axiosError.message ?? 'An error occurred',
      response?.status,
      response?.data,
    );
  }

  if (error instanceof Error) {
    return new ApiException(error.message);
  }

  return new ApiException('An unknown error occurred');
}

/**
 * Get user-friendly error message
 */
export function getErrorMessage(error: unknown): string {
  // Currency-guard errors (AP foreign-currency guards, GL choke-point rejections) carry
  // their own pt-BR userMessage/recoveryHint — prefer that over the raw dev-facing message
  // for any form that doesn't have bespoke handling (e.g. showCurrencyGuardErrorToast).
  const currencyPayload =
    extractCurrencyErrorPayload(parseApiError(error)) ?? extractCurrencyErrorPayload(error);
  if (currencyPayload) {
    const copy = getCurrencyErrorCopy(currencyPayload.code);
    const userMessage = currencyPayload.userMessage || copy?.userMessage;
    const recoveryHint = currencyPayload.recoveryHint ?? copy?.recoveryHint;
    if (userMessage) {
      return recoveryHint ? `${userMessage} ${recoveryHint}` : userMessage;
    }
  }

  // Kamino chart-of-accounts sync errors (ALC-2004) carry their own pt-BR copy — the
  // "no entity resolved" guard on `add_to_active` is a config mistake, not a dev-facing
  // 400, so it deserves an actionable message rather than the raw English exception text.
  const kaminoSyncPayload =
    extractKaminoSyncErrorPayload(parseApiError(error)) ?? extractKaminoSyncErrorPayload(error);
  if (kaminoSyncPayload) {
    const copy = getKaminoSyncErrorCopy(kaminoSyncPayload.code);
    if (copy) {
      return `${copy.userMessage} ${copy.recoveryHint}`;
    }
  }

  // Intangible-project refusals (ALC-3487): the review queue, the decision and the GL posting all
  // throw stable codes whose translated copy lives in `assetsIntangible.errors`. Read before the
  // generic paths below, which would otherwise surface the server's raw sentence.
  const intangibleCode =
    extractIntangibleErrorCode(parseApiError(error)) ?? extractIntangibleErrorCode(error);
  if (intangibleCode) {
    const copy = getIntangibleErrorCopy(intangibleCode);
    if (copy) {
      return copy;
    }
  }

  const apiError = parseApiError(error);
  if (apiError.details && typeof apiError.details === 'object') {
    const details = apiError.details as Record<string, unknown>;
    const userMessage =
      typeof details.userMessage === 'string' && details.userMessage.trim()
        ? details.userMessage.trim()
        : undefined;
    const recoveryHint =
      typeof details.recoveryHint === 'string' && details.recoveryHint.trim()
        ? details.recoveryHint.trim()
        : undefined;
    if (apiError.statusCode !== undefined && apiError.statusCode < 500 && userMessage) {
      return recoveryHint ? `${userMessage} ${recoveryHint}` : userMessage;
    }

    const code = typeof details.code === 'string' ? details.code : undefined;
    const mappedMessage = getValidationErrorMessageByCode(code);
    if (mappedMessage) return mappedMessage;
  }
  const message = sanitizeUserFacingMessage(apiError.message, apiError.statusCode);

  // Map common status codes to user-friendly messages
  switch (apiError.statusCode) {
    case 400:
      return message || 'Invalid request. Please check your input.';
    case 401:
      return 'Your session has expired. Please log in again.';
    case 403:
      return message || 'You do not have permission to perform this action.';
    case 404:
      return message || 'The requested resource was not found.';
    case 409:
      return message || 'A conflict occurred. The resource may already exist.';
    case 422:
      return message || 'Validation failed. Please check your input.';
    case 500:
      return message || 'A server error occurred. Please try again later.';
    case 503:
      return message || 'The service is temporarily unavailable. Please try again later.';
    default:
      return message || 'An error occurred. Please try again.';
  }
}

/** A save's error for the screen: the app sentence when the failure has no message of its own. */
export function getSaveErrorMessage(error: unknown): string {
  const status = parseApiError(error).statusCode;
  // A server error, a request that never got an answer (the network) or a rejection that is not
  // an error at all has nothing to tell the user; an error thrown with its own message keeps it.
  const network =
    typeof error === 'object' &&
    error !== null &&
    (error as { isAxiosError?: boolean }).isAxiosError === true &&
    !(error as { response?: unknown }).response;
  if (
    (status !== undefined && status >= 500) ||
    network ||
    (status === undefined && !(error instanceof Error))
  ) {
    return SERVER_ERROR_MESSAGE;
  }
  return getErrorMessage(error);
}

/** Hide leaked provider / internal operation details from end-user copy. */
/** The app's sentence for a server failure with no message of its own. */
export const SERVER_ERROR_MESSAGE =
  'Não foi possível concluir a operação. Tente novamente em instantes ou contate o suporte.';

function sanitizeUserFacingMessage(message: string, statusCode?: number): string {
  const looksInternal =
    /\bworkos\b/i.test(message) ||
    /\b(ensureOrganizationMembership|updateUserRoles|syncNewUserToWorkOS|syncUpdatedUserToWorkOS)\b/i.test(
      message,
    ) ||
    /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i.test(message);

  if (!looksInternal) {
    return message;
  }

  if (statusCode !== undefined && statusCode >= 500) {
    return SERVER_ERROR_MESSAGE;
  }

  return 'Não foi possível salvar. Verifique os dados informados e tente novamente.';
}

/**
 * Check if error is a specific status code
 */
export function isErrorStatus(error: unknown, statusCode: number): boolean {
  const apiError = parseApiError(error);
  return apiError.statusCode === statusCode;
}

/**
 * Check if error is a validation error (422)
 */
export function isValidationError(error: unknown): boolean {
  return isErrorStatus(error, 422);
}

/**
 * Check if error is unauthorized (401)
 */
export function isUnauthorizedError(error: unknown): boolean {
  return isErrorStatus(error, 401);
}

/**
 * Check if error is forbidden (403)
 */
export function isForbiddenError(error: unknown): boolean {
  return isErrorStatus(error, 403);
}

/**
 * Check if error is not found (404)
 */
export function isNotFoundError(error: unknown): boolean {
  return isErrorStatus(error, 404);
}

/**
 * Validation error from backend
 */
export interface ValidationErrorItem {
  field: string;
  message: string;
  code?: string;
}

/**
 * Extract validation errors from API error response.
 *
 * The backend sends structured validation errors as:
 * { errors: [{ field: '...', message: '...', code: '...' }, ...] }
 *
 * This function extracts these errors from the ApiException details.
 *
 * @param error The error to extract validation errors from
 * @returns Array of validation errors, or empty array if none found
 */
export function extractValidationErrors(error: unknown): ValidationErrorItem[] {
  const apiError = parseApiError(error);

  // Check if details contains errors array
  if (apiError.details && typeof apiError.details === 'object') {
    const details = apiError.details as Record<string, unknown>;

    // Handle { errors: [...] } format from backend BadRequestException
    if (Array.isArray(details.errors)) {
      return details.errors.filter(
        (item): item is ValidationErrorItem =>
          item !== null &&
          typeof item === 'object' &&
          'field' in item &&
          'message' in item &&
          typeof (item as Record<string, unknown>).field === 'string' &&
          typeof (item as Record<string, unknown>).message === 'string',
      );
    }
  }

  return [];
}
